import React, {useEffect, useState} from 'react';
import PlayerHeader from '../Player/PlayerHeader';
import Submenu from 'components/Navigation/Submenu/Submenu';
import {useTranslation} from 'react-i18next';
import {useReactiveVar} from '@apollo/client';
import {naviDataVar} from 'apollo/cashe';
import {firstDayOfMonth, lastDayOfMonth} from 'utils/date';
import {useTrainingsAttendance} from 'apollo/queries/TrainingQueries';
import Button from 'components/UI/Button/Button';
import DatePicker from 'components/UI/DatePicker/DatePicker';
import Panel from 'components/UI/Panel/Panel';
import Table from 'components/UI/Table/Table';
import DateTime from 'components/UI/Table/DateTime/DateTime';
import Subtext from 'components/UI/Table/Subtext/Subtext';
import {GuidTOUuid} from 'utils/utils';
import Dropdown from 'components/UI/Table/DropDown/Dropdown';

const PlayerAttendanceTrainings = (props) => {
    const {attendance, health} = props;

    const {t} = useTranslation();
    const navState = useReactiveVar(naviDataVar);

    const [playerId, setPlayerId] = useState(null);
    const [values, setValues] = useState({
        startDate: firstDayOfMonth(),
        endDate: lastDayOfMonth()
    });

    const [getTrainingsAttendance, trainingsAttendanceData] = useTrainingsAttendance();

    useEffect(() => {
        if (!navState.clubId) return;

        getTrainingsAttendance({ variables: {
                "clubId": navState.clubId,
                "teamId": navState.teamId,
                "where": {
                    "start": {"gte": values.startDate},
                    "end": {"lte": values.endDate}
                }
            }});

        setPlayerId(GuidTOUuid(navState.playerId));

    }, [navState, values]);

    const actions = () => {
        return (
            <Button type="primary" style={{margin: 0}}>
                <span><i className="icon-Send"></i> {t("Share")}</span>
            </Button>
        )
    }

    const onFilter = (e) => {
        const newFilters = {...values};
        newFilters[e.target.name] = e.target.value;
        setValues(newFilters);
    }

    const filters = () => {
        return (
            <>
                <DatePicker
                    name="startDate"
                    onChange={onFilter}
                    placeholder={t("Date from")}
                    value={values}
                />

                <DatePicker
                    name="endDate"
                    onChange={onFilter}
                    placeholder={t("Date till")}
                    value={values}
                />
            </>
        )
    }

    const getHealthIcon = (val) => {
        const  status = health.find(x => x.value === val);
        return status?.icon ? <span dangerouslySetInnerHTML={{__html: status?.icon}}></span> : "";
    }

    return (
        <>
            <div className="page-header">
                <PlayerHeader />
                <Submenu/>
            </div>
            <div className="page-content">
                <Panel border={false} action={actions} filters={filters} />

                {trainingsAttendanceData.loading && <>Loading...</>}
                {trainingsAttendanceData.error && <>Error...</>}
                {trainingsAttendanceData.data && <Table
                    data={trainingsAttendanceData.data.trainings.items}
                    center="true"
                    columns={[
                        {
                            Header: t('Date'),
                            accessor: 'start',
                            Cell: cellData => <DateTime data={cellData.value} />
                        },
                        {
                            Header: t('Name'),
                            accessor: 'name',
                            Cell: cellData => {
                                const {trainingNumber, block} = cellData.row.original;
                                return (<>
                                    {cellData.value} {trainingNumber ? ", " + trainingNumber: undefined}
                                    <Subtext>{t("Block:")} {block ? block : "-"}</Subtext>
                                </>);
                            }
                        },
                        {
                            Header: t('Attendance'),
                            accessor: 'attendance',
                            Cell: cellData => {
                                const {players} = cellData.row.original;
                                const player = players.find(x => x.playerId == playerId);
                                return (<>
                                    {player ? <Dropdown name="status"
                                                        onChange={() => {}}
                                                        options={attendance} value={player?.status} /> : "-"}
                                </>);
                            }
                        },
                        {
                            Header: t('Before'),
                            accessor: 'before',
                            Cell: cellData => {
                                const {players} = cellData.row.original;
                                const player = players.find(x => x.playerId == playerId);
                                return (<>
                                    {player ? getHealthIcon(player?.healthAfter) : undefined}
                                </>);
                            }
                        },
                        {
                            Header: t('After'),
                            accessor: 'after',
                            Cell: cellData => {
                                const {players} = cellData.row.original;
                                const player = players.find(x => x.playerId == playerId);
                                return (<>
                                    {player ? getHealthIcon(player?.healthBefore) : undefined}
                                </>);
                            }
                        },
                        {
                            Header: t('Rating'),
                            accessor: 'rating',
                            Cell: cellData => {
                                const {players} = cellData.row.original;
                                const player = players.find(x => x.playerId == playerId);
                                return (<>
                                    {player ? player?.rating : undefined}
                                </>);
                            }
                        }
                    ]}
                    onRowClick={() => {}} />}
            </div>
        </>
    );
};

PlayerAttendanceTrainings.defaultProps = {
    attendance: [
        {label: "Present", icon: "<i class=\"icon-Check-Square icon-green\"></i>", value: "PRESENT"},
        {label: "Was late", icon: "<i class=\"icon-Time-Square icon-yellow\"></i>", value: "WAS_LATE"},
        {label: "Ill", icon: "<i class=\"icon-medicine icon-blue\"></i>", value: "ILL"},
        {label: "Excused", icon: "<i class=\"icon-Document icon-blue\"></i>", value: "EXCUSED"},
        {label: "Unexcused", icon: "<i class=\"icon-Close-Square icon-red\"></i>", value: "UNEXCUSED"},
    ],
    health: [
        {label: "Good", icon: "<span class=\"icon-smHappy\"><span class=\"path1\"></span><span class=\"path2\"></span><span class=\"path3\"></span><span class=\"path4\"></span><span class=\"path5\"></span><span class=\"path6\"></span></span>", value: "GOOD"},
        {label: "Average", icon: "<span class=\"icon-smConfused\"><span class=\"path1\"></span><span class=\"path2\"></span><span class=\"path3\"></span><span class=\"path4\"></span><span class=\"path5\"></span><span class=\"path6\"></span></span>", value: "AVERAGE"},
        {label: "Bad", icon: "<span class=\"icon-smSad\"><span class=\"path1\"></span><span class=\"path2\"></span><span class=\"path3\"></span><span class=\"path4\"></span><span class=\"path5\"></span><span class=\"path6\"></span><span class=\"path7\"></span><span class=\"path8\"></span><span class=\"path9\"></span></span>", value: "BAD"},
    ],
};

export default PlayerAttendanceTrainings;
